import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { useAsu } from "@framework/provider-asu";
import { useApiCall } from "@framework/react-hooks";

import { lineColors } from "../../scheme/components/analytics/constants";
import {
  ANALYTICS_MEASURE_TYPE,
  type IChartData,
  type IChartDataOrbit,
  type IChartDataSignal,
  type ILastMeasureDataAutocompleteResponse,
  type IMeasuresResponse,
  type IOrbitDataResponse,
  type ISignalDataResponse,
} from "../../scheme/components/analytics/interfaces";
import {
  transformAmplitudePhaseToOrbit,
  transformOrbitDataToChart,
} from "../../scheme/components/analytics/mappers/orbitMappers";
import { transformSingleSignalToChart } from "../../scheme/components/analytics/mappers/signalMappers";
import type { IMultiAnalysisTemplateCell } from "../utils/analysis-template-settings";
import { matchesTemplateFilters, resolveDataMarkMeasureDate } from "../utils/data-marks";
import { mapMultiAnalysisMeasureTypeToAnalytics } from "../utils/measure-type-map";
import { fetchMultiAnalysisMeasureData, fetchMultiAnalysisMeasureOptions } from "../utils/measure-options";
import {
  buildMultiAnalysisTrendSeries,
  filterMeasureResponseByCellFilters,
  normalizeTrendRangeForRequest,
  type IMultiAnalysisMeasureDataResponse,
  type IMultiAnalysisMeasureOption,
  type IMultiAnalysisRequestTimeRange,
} from "../utils/trend-series";
import { parseMultiAnalysisChartTime } from "../utils/range-frame";
import {
  MULTI_ANALYSIS_MEASURE_TYPE,
  type IMultiAnalysisDataMark,
  type IMultiAnalysisTimeRange,
  type IStructureTreePoint,
} from "../types";
import { normalizeResponseArray } from "../utils/response-array";
import { fetchMultiAnalysisSignalData } from "../utils/signal-data";
import { fetchMultiAnalysisOrbitData } from "../utils/orbit-data";

export type TMultiAnalysisGraphicAreaCellData =
  | {
      kind: "signal";
      data: IChartDataSignal[];
    }
  | {
      kind: "orbit" | "polar";
      data: IChartDataOrbit[];
    };

interface IUseMultiAnalysisGraphicAreaDataArgs {
  enabled: boolean;
  cells: readonly IMultiAnalysisTemplateCell[];
  points: readonly IStructureTreePoint[];
  range: IMultiAnalysisTimeRange;
  selectedDataMarks: readonly IMultiAnalysisDataMark[];
}

interface ILoadGraphicAreaDataArgs {
  cells: readonly IMultiAnalysisTemplateCell[];
  points: readonly IStructureTreePoint[];
  range: IMultiAnalysisRequestTimeRange;
  selectedDataMarks: readonly IMultiAnalysisDataMark[];
}

interface ILastMeasureGroup {
  name: string;
  data: ILastMeasureDataAutocompleteResponse[];
}

interface IMeasureSelection {
  idMeasure: number;
  idPoint: number;
  measureName: string;
  date: string;
  param1: number | null;
  param2: number | null;
}

type TLastMeasuresResponse = Record<string, ILastMeasureGroup | undefined>;

interface IFormatters {
  unitLabelFormatter: (unitCode: number) => string;
  pointDirectionLabelFormatter: (direction: number) => string;
}

const buildGraphicAreaRequestKey = (
  cells: readonly IMultiAnalysisTemplateCell[],
  points: readonly IStructureTreePoint[],
  range: IMultiAnalysisRequestTimeRange,
  selectedDataMarks: readonly IMultiAnalysisDataMark[],
): string =>
  JSON.stringify({
    cells: cells.map(cell => ({
      id: cell.id,
      measureType: cell.measureType,
      filters: cell.filters,
    })),
    points: points.map(point => point.id),
    range,
    selectedDataMarks: selectedDataMarks.map(mark => ({
      key: mark.key,
      idMeasure: mark.idMeasure,
      measureDate: resolveDataMarkMeasureDate(mark),
      date: mark.date,
      idPoint: mark.idPoint,
    })),
  });

const toTimestamp = (value: string): number => {
  const timestamp = parseMultiAnalysisChartTime(value);

  return Number.isFinite(timestamp) ? timestamp : 0;
};

const formatUnit = (unitCode: unknown, formatters: IFormatters): string => {
  const numericCode = Number(unitCode);

  return Number.isFinite(numericCode) ? formatters.unitLabelFormatter(numericCode) : "";
};

const getPointName = (point: IStructureTreePoint | undefined, formatters: IFormatters): string => {
  if (!point) {
    return "";
  }

  return `${point.title}${
    Number.isFinite(point.direction) ? ` ${formatters.pointDirectionLabelFormatter(point.direction)}` : ""
  }`;
};

const getMeasureTitle = (measureName: string, measureOptions: readonly IMultiAnalysisMeasureOption[]): string => {
  const option = measureOptions.find(item => item.title === measureName);

  return option?.description && option.description !== measureName ? option.description : measureName;
};

const getUniqueMeasureNames = (measureOptions: readonly IMultiAnalysisMeasureOption[]): string[] =>
  Array.from(new Set(measureOptions.map(({ title }) => title)));

const filterMeasureOptionsByCellFilters = (
  measureOptions: readonly IMultiAnalysisMeasureOption[],
  filters: Record<string, unknown>,
): IMultiAnalysisMeasureOption[] =>
  measureOptions.filter(option =>
    matchesTemplateFilters(
      {
        Name: option.title,
        RangeType: option.rangeType,
        Type: option.type,
      },
      filters,
    ),
  );

const selectMeasurementCandidates = (
  response: TLastMeasuresResponse | null | undefined,
  filters: Record<string, unknown>,
): IMeasureSelection[] => {
  if (!response) {
    return [];
  }

  const latestByMeasureAndPoint = new Map<string, IMeasureSelection>();

  Object.values(response).forEach(group => {
    if (!group?.name || !Array.isArray(group.data)) {
      return;
    }

    group.data.forEach(measure => {
      if (
        !matchesTemplateFilters(
          {
            Name: group.name,
            Units: measure.units,
            param1: measure.param1 ?? null,
            param2: measure.param2 ?? null,
          },
          filters,
        )
      ) {
        return;
      }

      const key = `${measure.idMeasure}:${measure.idPoint}`;
      const nextSelection: IMeasureSelection = {
        idMeasure: measure.idMeasure,
        idPoint: measure.idPoint,
        measureName: group.name,
        date: measure.measureDate,
        param1: measure.param1 ?? null,
        param2: measure.param2 ?? null,
      };
      const previous = latestByMeasureAndPoint.get(key);

      if (!previous || toTimestamp(nextSelection.date) > toTimestamp(previous.date)) {
        latestByMeasureAndPoint.set(key, nextSelection);
      }
    });
  });

  return Array.from(latestByMeasureAndPoint.values()).sort(
    (left, right) => toTimestamp(right.date) - toTimestamp(left.date),
  );
};

const markToMeasureRow = (mark: IMultiAnalysisDataMark): IMeasuresResponse => ({
  idMeasure: mark.idMeasure,
  Date: mark.date,
  Value1: mark.value,
  Value2: mark.value2,
  idPoint: mark.idPoint,
  Units: mark.units,
  Name: mark.measureName,
  AlarmLevel2: null,
  AlarmLevel3: null,
  AlarmLevel4: null,
  AlarmType: 0,
  RPM: mark.rotationFrequencyRpm,
  Param1: mark.param1,
  Param2: mark.param2,
});

const buildMeasureFilterSource = (
  measurement: IMeasureSelection | undefined,
  values: Record<string, unknown>,
): Record<string, unknown> => {
  const source = { ...values };

  if (measurement) {
    source.param1 = measurement.param1;
    source.param2 = measurement.param2;
  }

  return source;
};

const indexMeasurementsById = (measurements: readonly IMeasureSelection[]): Map<number, IMeasureSelection> =>
  new Map(measurements.map(measurement => [measurement.idMeasure, measurement]));

const markToMeasureSelection = (mark: IMultiAnalysisDataMark): IMeasureSelection => ({
  idMeasure: mark.idMeasure,
  idPoint: mark.idPoint,
  measureName: mark.measureName,
  date: resolveDataMarkMeasureDate(mark),
  param1: mark.param1,
  param2: mark.param2,
});

const buildSelectedMarksTrendResponse = (
  marks: readonly IMultiAnalysisDataMark[],
  measureOptions: readonly IMultiAnalysisMeasureOption[],
): IMultiAnalysisMeasureDataResponse => {
  const marksByMeasureName = new Map<string, IMeasuresResponse[]>();

  marks.forEach(mark => {
    marksByMeasureName.set(mark.measureName, [
      ...(marksByMeasureName.get(mark.measureName) ?? []),
      markToMeasureRow(mark),
    ]);
  });

  return measureOptions.reduce<IMultiAnalysisMeasureDataResponse>((acc, option, index) => {
    const data = marksByMeasureName.get(option.title);

    if (data?.length) {
      acc[`measureData${index + 1}`] = {
        name: option.title,
        data,
      };
    }

    return acc;
  }, {});
};

const getSelectedMarksForCell = (
  selectedDataMarks: readonly IMultiAnalysisDataMark[],
  pointIds: ReadonlySet<number>,
  measureNames: readonly string[],
  filters: Record<string, unknown>,
): IMultiAnalysisDataMark[] => {
  if (!selectedDataMarks.length) {
    return [];
  }

  const measureNameSet = new Set(measureNames);

  return selectedDataMarks.filter(
    mark =>
      pointIds.has(mark.idPoint) &&
      measureNameSet.has(mark.measureName) &&
      matchesTemplateFilters(
        {
          Name: mark.measureName,
          Units: mark.units,
          param1: mark.param1,
          param2: mark.param2,
        },
        filters,
      ),
  );
};

const getUniqueMeasureRefs = (items: readonly IMeasureSelection[]): Array<{ idMeasure: number; date: string }> => {
  const seen = new Set<string>();

  return items.flatMap(item => {
    const key = `${item.idMeasure}:${item.date}`;

    if (seen.has(key)) {
      return [];
    }

    seen.add(key);

    return [
      {
        idMeasure: item.idMeasure,
        date: item.date,
      },
    ];
  });
};

const getMeasureRefKey = ({ idMeasure, date }: { idMeasure: number; date: string }): string => {
  const timestamp = parseMultiAnalysisChartTime(date);

  return `${idMeasure}:${Number.isFinite(timestamp) ? timestamp : date}`;
};

const selectLatestSignals = (signals: readonly ISignalDataResponse[]): ISignalDataResponse[] => {
  const latestByMeasureAndPoint = new Map<string, ISignalDataResponse>();

  signals.forEach(signal => {
    const key = `${signal.idMeasure}:${signal.idPoint}`;
    const previous = latestByMeasureAndPoint.get(key);

    if (!previous || toTimestamp(signal.date) > toTimestamp(previous.date)) {
      latestByMeasureAndPoint.set(key, signal);
    }
  });

  return Array.from(latestByMeasureAndPoint.values()).sort(
    (left, right) => toTimestamp(right.date) - toTimestamp(left.date),
  );
};

const selectLatestOrbitResponses = (
  orbitResponses: readonly { measurement: IMeasureSelection; orbit: IOrbitDataResponse }[],
): Array<{ measurement: IMeasureSelection; orbit: IOrbitDataResponse }> => {
  const latestByMeasureAndPoint = new Map<string, { measurement: IMeasureSelection; orbit: IOrbitDataResponse }>();

  orbitResponses.forEach(item => {
    const key = `${item.measurement.idMeasure}:${item.measurement.idPoint}`;
    const previous = latestByMeasureAndPoint.get(key);
    const itemDate = item.orbit.date || item.measurement.date;
    const previousDate = previous ? previous.orbit.date || previous.measurement.date : "";

    if (!previous || toTimestamp(itemDate) > toTimestamp(previousDate)) {
      latestByMeasureAndPoint.set(key, item);
    }
  });

  return Array.from(latestByMeasureAndPoint.values()).sort((left, right) => {
    const leftDate = left.orbit.date || left.measurement.date;
    const rightDate = right.orbit.date || right.measurement.date;

    return toTimestamp(rightDate) - toTimestamp(leftDate);
  });
};

const normalizeSignalChart = (
  signal: ISignalDataResponse,
  index: number,
  measureType: ANALYTICS_MEASURE_TYPE,
  pointsById: ReadonlyMap<number, IStructureTreePoint>,
  measureOptions: readonly IMultiAnalysisMeasureOption[],
  useMeasureTitle: boolean,
  formatters: IFormatters,
): IChartDataSignal => {
  const pointName = getPointName(pointsById.get(signal.idPoint), formatters) || signal.pointName;
  const measureTitle = getMeasureTitle(signal.name, measureOptions);
  const chart = transformSingleSignalToChart(signal, pointName, measureType);

  return {
    ...chart,
    items: chart.items,
    color: lineColors[index % lineColors.length],
    name: useMeasureTitle ? `${pointName} / ${measureTitle}` : pointName,
    unitsXCode: chart.unitsXCode ?? null,
    unitsYCode: chart.unitsYCode ?? null,
    unitsX: formatUnit(chart.unitsXCode ?? chart.unitsX, formatters),
    unitsY: formatUnit(chart.unitsYCode ?? chart.unitsY, formatters),
  };
};

const normalizeOrbitChart = (
  orbit: IOrbitDataResponse,
  index: number,
  measurement: IMeasureSelection,
  pointsById: ReadonlyMap<number, IStructureTreePoint>,
  measureOptions: readonly IMultiAnalysisMeasureOption[],
  useMeasureTitle: boolean,
  formatters: IFormatters,
): IChartDataOrbit => {
  const pointName = getPointName(pointsById.get(measurement.idPoint), formatters) || orbit.name;
  const measureTitle = getMeasureTitle(measurement.measureName, measureOptions);
  const chart = transformOrbitDataToChart(orbit, index);

  return {
    ...chart,
    idPoint: measurement.idPoint,
    parameterName: measurement.measureName,
    unitCode: chart.unitCode ?? null,
    name: useMeasureTitle ? `${pointName} / ${measureTitle}` : pointName,
    units: formatUnit(chart.unitCode ?? chart.units, formatters),
  };
};

export const useMultiAnalysisGraphicAreaData = ({
  enabled,
  cells,
  points,
  range,
  selectedDataMarks,
}: IUseMultiAnalysisGraphicAreaDataArgs): {
  dataByCellId: Record<string, TMultiAnalysisGraphicAreaCellData | null>;
  isLoading: boolean;
} => {
  const { formatMessage } = useIntl();
  const { dbTz } = useAsu();
  const [dataByCellId, setDataByCellId] = useState<Record<string, TMultiAnalysisGraphicAreaCellData | null>>({});
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);
  const lastRequestKeyRef = useRef<string | null>(null);

  const formatters: IFormatters = {
    unitLabelFormatter: unitCode => formatMessage({ id: `enums.units.${unitCode}` }),
    pointDirectionLabelFormatter: direction => formatMessage({ id: `enums.pointDirection.${direction}` }),
  };

  const { fn: loadGraphicAreaData } = useApiCall(
    async (
      api,
      {
        cells: requestCells,
        points: requestPoints,
        range: requestRange,
        selectedDataMarks: requestMarks,
      }: ILoadGraphicAreaDataArgs,
    ) => {
      const pointIds = requestPoints.map(({ id }) => id);
      const pointIdSet = new Set(pointIds);
      const pointsById = new Map(requestPoints.map(point => [point.id, point]));
      const lastMeasuresCache = new Map<string, Promise<TLastMeasuresResponse | null | undefined>>();
      const measureOptionsCache = new Map<
        ANALYTICS_MEASURE_TYPE,
        ReturnType<typeof fetchMultiAnalysisMeasureOptions>
      >();

      const buildLastMeasuresCacheKey = (
        measureType: ANALYTICS_MEASURE_TYPE,
        measureNames: readonly string[],
      ): string => `${measureType}:${measureNames.join("\u0000")}`;

      const fetchMeasureOptionsCached = (measureType: ANALYTICS_MEASURE_TYPE) => {
        const cached = measureOptionsCache.get(measureType);

        if (cached) {
          return cached;
        }

        const request = fetchMultiAnalysisMeasureOptions(api, measureType, pointIds);
        measureOptionsCache.set(measureType, request);

        return request;
      };

      const fetchLastMeasures = (
        measureType: ANALYTICS_MEASURE_TYPE,
        measureNames: readonly string[],
      ): Promise<TLastMeasuresResponse | null | undefined> => {
        const cacheKey = buildLastMeasuresCacheKey(measureType, measureNames);
        const cached = lastMeasuresCache.get(cacheKey);

        if (cached) {
          return cached;
        }

        const request = api.fetchJson({
          url: "/point-v2/latest-measures",
          method: "GET",
          data: {
            points: JSON.stringify(pointIds),
            measureNames: JSON.stringify(measureNames),
            startTimestamp: requestRange.startTimestamp,
            endTimestamp: requestRange.endTimestamp,
            measureType,
          },
        }) as Promise<TLastMeasuresResponse | null | undefined>;

        lastMeasuresCache.set(cacheKey, request);

        return request;
      };

      const fetchMeasureData = (
        measureType: ANALYTICS_MEASURE_TYPE,
        measureNames: readonly string[],
      ): Promise<IMultiAnalysisMeasureDataResponse | null | undefined> =>
        fetchMultiAnalysisMeasureData(api, {
          measureType,
          pointIds,
          measureNames,
          range: requestRange,
        });

      const fetchSignalData = (measureRefs: Array<{ idMeasure: number; date: string }>) =>
        fetchMultiAnalysisSignalData(api, measureRefs);

      const fetchOrbitData = (measureRefs: Array<{ idMeasure: number; date: string }>) =>
        fetchMultiAnalysisOrbitData(api, measureRefs);

      const loadSignalCellData = async (
        cell: IMultiAnalysisTemplateCell,
        measureType: ANALYTICS_MEASURE_TYPE,
      ): Promise<TMultiAnalysisGraphicAreaCellData | null> => {
        const measureOptions = filterMeasureOptionsByCellFilters(
          await fetchMeasureOptionsCached(measureType),
          cell.filters,
        );
        const measureNames = getUniqueMeasureNames(measureOptions);

        if (!measureNames.length) {
          return null;
        }

        const selectedMarks = getSelectedMarksForCell(requestMarks, pointIdSet, measureNames, cell.filters);
        const useExplicitMarks = selectedMarks.length > 0;

        const measurements = useExplicitMarks
          ? selectedMarks.map(markToMeasureSelection)
          : selectMeasurementCandidates(await fetchLastMeasures(measureType, measureNames), cell.filters);
        const measurementById = indexMeasurementsById(measurements);
        const measureRefs = getUniqueMeasureRefs(measurements);

        if (!measureRefs.length) {
          return null;
        }

        const filteredSignals = (await fetchSignalData(measureRefs)).filter(signal =>
          matchesTemplateFilters(
            buildMeasureFilterSource(measurementById.get(signal.idMeasure), {
              Name: signal.name,
              RangeType: signal.rangeType,
              Units: signal.units,
              Resolution: signal.resolution,
            }),
            cell.filters,
          ),
        );
        const signals = useExplicitMarks ? filteredSignals : selectLatestSignals(filteredSignals);

        const data = signals.map((signal, index) =>
          normalizeSignalChart(
            signal,
            index,
            measureType,
            pointsById,
            measureOptions,
            measureNames.length > 1,
            formatters,
          ),
        );

        return data.length > 0 ? { kind: "signal", data } : null;
      };

      const loadOrbitCellData = async (
        cell: IMultiAnalysisTemplateCell,
      ): Promise<TMultiAnalysisGraphicAreaCellData | null> => {
        const measureType = ANALYTICS_MEASURE_TYPE.ORBIT;
        const measureOptions = filterMeasureOptionsByCellFilters(
          await fetchMeasureOptionsCached(measureType),
          cell.filters,
        );
        const measureNames = getUniqueMeasureNames(measureOptions);

        if (!measureNames.length) {
          return null;
        }

        const selectedMarks = getSelectedMarksForCell(requestMarks, pointIdSet, measureNames, cell.filters);
        const useExplicitMarks = selectedMarks.length > 0;

        const measurements = useExplicitMarks
          ? selectedMarks.map(markToMeasureSelection)
          : selectMeasurementCandidates(await fetchLastMeasures(measureType, measureNames), cell.filters);

        if (!measurements.length) {
          return null;
        }

        const measureRefs = getUniqueMeasureRefs(measurements);
        const orbitResponsesByKey = new Map(
          normalizeResponseArray(await fetchOrbitData(measureRefs)).map(orbit => [getMeasureRefKey(orbit), orbit]),
        );
        const orbitResponses = measurements.map(measurement => {
          const orbit = orbitResponsesByKey.get(getMeasureRefKey(measurement));

          return orbit ? { measurement, orbit } : null;
        });

        const filteredOrbitResponses = orbitResponses
          .filter(
            (
              item,
            ): item is {
              measurement: IMeasureSelection;
              orbit: IOrbitDataResponse;
            } => item !== null,
          )
          .filter(({ measurement, orbit }) =>
            matchesTemplateFilters(
              buildMeasureFilterSource(measurement, {
                Name: orbit.name,
                RangeType: orbit.rangeType,
                Units: orbit.units,
                Resolution: orbit.resolution,
                Param3: orbit.param3,
                Param3DataMs: orbit.param3DataMs,
              }),
              cell.filters,
            ),
          );
        const sourceOrbitResponses = useExplicitMarks
          ? filteredOrbitResponses
          : selectLatestOrbitResponses(filteredOrbitResponses);
        const data = sourceOrbitResponses.map(({ measurement, orbit }, index) =>
          normalizeOrbitChart(
            orbit,
            index,
            measurement,
            pointsById,
            measureOptions,
            measureNames.length > 1,
            formatters,
          ),
        );

        return data.length > 0 ? { kind: "orbit", data } : null;
      };

      const loadPolarCellData = async (
        cell: IMultiAnalysisTemplateCell,
      ): Promise<TMultiAnalysisGraphicAreaCellData | null> => {
        const measureType = ANALYTICS_MEASURE_TYPE.TREND;
        const measureOptions = filterMeasureOptionsByCellFilters(
          (await fetchMeasureOptionsCached(measureType)).filter(({ useValue2 }) => useValue2),
          cell.filters,
        );
        const measureNames = getUniqueMeasureNames(measureOptions);

        if (!measureNames.length) {
          return null;
        }

        const selectedMarks = getSelectedMarksForCell(requestMarks, pointIdSet, measureNames, cell.filters);
        const useExplicitMarks = selectedMarks.length > 0;

        const response = useExplicitMarks
          ? buildSelectedMarksTrendResponse(selectedMarks, measureOptions)
          : (await fetchMeasureData(measureType, measureNames)) ?? {};
        const filteredResponse = filterMeasureResponseByCellFilters(response, cell.filters);
        const trendSeries: IChartData[] = buildMultiAnalysisTrendSeries(
          filteredResponse,
          requestPoints,
          measureOptions,
          {
            unitLabelFormatter: formatters.unitLabelFormatter,
            pointDirectionLabelFormatter: formatters.pointDirectionLabelFormatter,
          },
        );
        const data = transformAmplitudePhaseToOrbit(trendSeries);

        return data.length > 0 ? { kind: "polar", data } : null;
      };

      const loadCellData = async (
        cell: IMultiAnalysisTemplateCell,
      ): Promise<[string, TMultiAnalysisGraphicAreaCellData | null]> => {
        try {
          switch (cell.measureType) {
            case MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM:
            case MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM:
              return [
                cell.id,
                await loadSignalCellData(cell, mapMultiAnalysisMeasureTypeToAnalytics(cell.measureType)),
              ];
            case MULTI_ANALYSIS_MEASURE_TYPE.ORBIT:
              return [cell.id, await loadOrbitCellData(cell)];
            case MULTI_ANALYSIS_MEASURE_TYPE.POLAR_TREND:
              return [cell.id, await loadPolarCellData(cell)];
            default:
              return [cell.id, null];
          }
        } catch (error) {
          console.error(error);

          return [cell.id, null];
        }
      };

      const entries = await Promise.all(requestCells.map(loadCellData));

      return entries.reduce<Record<string, TMultiAnalysisGraphicAreaCellData | null>>((acc, [cellId, data]) => {
        acc[cellId] = data;
        return acc;
      }, {});
    },
    { success: false, error: false },
  );
  const loadGraphicAreaDataRef = useRef(loadGraphicAreaData);

  useEffect(() => {
    loadGraphicAreaDataRef.current = loadGraphicAreaData;
  }, [loadGraphicAreaData]);

  useEffect(() => {
    const requestRange = normalizeTrendRangeForRequest(range, dbTz);

    if (!enabled || !cells.length || !points.length || !requestRange) {
      requestIdRef.current += 1;
      lastRequestKeyRef.current = null;
      setDataByCellId({});
      setIsLoading(false);
      return;
    }

    const requestKey = buildGraphicAreaRequestKey(cells, points, requestRange, selectedDataMarks);

    if (lastRequestKeyRef.current === requestKey) {
      return;
    }

    lastRequestKeyRef.current = requestKey;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);

    void loadGraphicAreaDataRef
      .current(undefined, {
        cells,
        points,
        range: requestRange,
        selectedDataMarks,
      })
      .then(nextDataByCellId => {
        if (requestIdRef.current === requestId) {
          setDataByCellId(nextDataByCellId ?? {});
        }
      })
      .catch(error => {
        console.error(error);

        if (requestIdRef.current === requestId) {
          lastRequestKeyRef.current = null;
          setDataByCellId({});
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });
  }, [cells, dbTz, enabled, formatMessage, points, range, selectedDataMarks]);

  return { dataByCellId, isLoading };
};
